import React from 'react';
import axios from 'axios';
import { Link }  from 'react-router-dom';
import { format as dateFormat } from 'date-fns';

import './FlowTable.css';

import { Histogram } from '../common/histogram/Histogram';
import { useAuth } from '../common/auth/use-auth';
import { JetTable } from '../jet-ui/JetTable/JetTable';
import { Column } from 'react-table';
import { Breakpoint, Large, Small } from '../jet-ui/Breakpoints/Breakpoint';
import { Card } from '../jet-ui/Card/Card';
import { Layout } from '../jet-ui/Layout/Layout';
import { LayoutItem } from '../jet-ui/LayoutItem/LayoutItem';
import { Spinner } from '../jet-ui/Spinner/Spinner';
import { ApiResult, Project } from '../common/dataTypes/Jetstream';
import { DateRangeFilter } from '../common/DateRangeFilter/DateRangeFilter';
import { MultiSelectFilter } from '../common/MultiSelectFilter/MultiSelectFilter';
import { useArrayQueryParam, useStringQueryParam } from '../common/hooks/useQueryFilters';
import { useDisplayPreferences } from '../common/hooks/useDisplayPreferences';

interface FlowTableProps {
    pageIndex?: number;
    onManualPageChangeFn?: (page: number) => void;
};

/** A component that displays a table of currently registered flows. */
export const FlowTable: React.FC<FlowTableProps> = (props) => {

  const [flows, setFlows] = React.useState<any[]>([]);
  const [allFlowNames, setAllFlowNames] = React.useState<string[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const { prefs } = useDisplayPreferences();
  const [projectFilter, setProjectFilter] = useArrayQueryParam('project');
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [flowFilter, setFlowFilter] = useArrayQueryParam('flow');
  const [lastRunFrom, setLastRunFrom] = useStringQueryParam('lastRunFrom');
  const [lastRunTo, setLastRunTo] = useStringQueryParam('lastRunTo');

  const auth = useAuth();
  const onManualPageChange = React.useCallback((pageIndex: number) => {
    if (props.onManualPageChangeFn != undefined) {
        props.onManualPageChangeFn(pageIndex);
    }
    return;
  }, []);

  // Fetch projects list
  React.useEffect(() => {
    axios.get<ApiResult<Project[]>>('/api/projects', auth.getRequestConfig())
      .then(res => setProjects(res.data.data))
      .catch(() => {});
  }, [auth.user]);

  // Fetch scoped flow names for the Flow filter dropdown whenever project filter changes
  React.useEffect(() => {
    let cancelled = false;
    let url = '/api/flows';
    if (projectFilter.length > 0) {
      url += '?' + projectFilter.map(p => `projectName=${encodeURIComponent(p)}`).join('&');
    }
    axios.get<ApiResult<any>>(url, auth.getRequestConfig())
      .then(res => {
        if (cancelled) return;
        const names = res.data.data.flows.map((f: any) => f.structure.name).sort();
        setAllFlowNames(names);
        setFlowFilter(prev => prev.filter(f => names.includes(f)));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [auth.user, projectFilter]);

  const fetchData = React.useCallback(async () => {
    setIsLoading(true);

    try {
      let url = '/api/flows';
      const params: string[] = [];
      projectFilter.forEach(p => params.push(`projectName=${encodeURIComponent(p)}`));
      flowFilter.forEach(f => params.push(`flowName=${encodeURIComponent(f)}`));
      if (lastRunFrom) params.push(`lastRunFrom=${encodeURIComponent(lastRunFrom)}`);
      if (lastRunTo) params.push(`lastRunTo=${encodeURIComponent(lastRunTo)}`);
      if (params.length > 0) url += '?' + params.join('&');
      const result = await axios(url, auth.getRequestConfig());
      setFlows(result.data.data.flows.map((flow: any) => {
        return {
          paused: false,
          name: flow.structure.name,
          project: flow.project ?? '',
          jobs: flow.structure.jobs.length,
          schedule: flow.schedule,
          next: flow.next,
          last: flow.last,
          histogram: flow.histogram
        }
      }));
    }
    finally {
      setIsLoading(false);
    }

  }, [auth.user, projectFilter, flowFilter, lastRunFrom, lastRunTo]);

  React.useEffect(() => {
    fetchData();
  }, [auth.user, fetchData]);

  const columns: Column<any>[] = React.useMemo(() => [
    { Header: () => <MultiSelectFilter label="Flow" options={allFlowNames} selected={flowFilter} onChange={setFlowFilter} />,
      accessor:'name', disableSortBy: true, Cell: ({value}) => <Link to={`/flow/${encodeURIComponent(value)}/detail`}>{value}</Link>},
    { Header: () => <MultiSelectFilter label="Project" options={projects.map(p => p.name)} selected={projectFilter} onChange={setProjectFilter} />,
      accessor:'project', disableSortBy: true },
    { Header: 'Jobs', accessor:'jobs', width: '7%' },
    { Header: 'Schedule', accessor:'schedule', disableSortBy: true },
    { Header: 'Up Next', accessor:'next', disableSortBy: true },
    { Header: () => <DateRangeFilter label="Last Run" fromDate={lastRunFrom} toDate={lastRunTo} onChange={(from, to) => { setLastRunFrom(from); setLastRunTo(to); }} />,
      accessor:'last', disableSortBy: true },
    { Header: 'Histogram', accessor:'histogram', disableSortBy: true, Cell: ({value}) => <div className="FlowTable__Histogram"><Histogram data={value} /></div> },
  ], [projects, projectFilter, allFlowNames, flowFilter, lastRunFrom, lastRunTo]);

  return (
    <Breakpoint>
      <Small>
        {isLoading && <div className="FlowTable__Loading"><Spinner /></div>}
        {flows.map(flow => (
          <Card key={flow.name} className="FlowTable__Card">
            <Layout>
              <LayoutItem sm={8}>
                <h4><Link to={`/flow/${encodeURIComponent(flow.name)}/detail`}>{flow.name}</Link></h4>
                {flow.project && <p><b>Project</b> | {flow.project}</p>}
                <p>
                  <b>Schedule</b> | {flow.schedule}
                </p>
                <p>
                  <b>Up Next</b> | {dateFormat(new Date(flow.next), 'yyyy-MM-dd HH:mm:ss')}
                </p>
                <p>
                  <b>Last Run</b> | {dateFormat(new Date(flow.last), 'yyyy-MM-dd HH:mm:ss')}
                </p>
              </LayoutItem>
              <LayoutItem className="FlowTable__CardHistogram" sm={4}>
                <Histogram data={flow.histogram} />
              </LayoutItem>
            </Layout>
          </Card>
        ))}
      </Small>
      <Large>
        <JetTable className="FlowTable" data={flows} columns={columns} loading={isLoading} rowsPerPage={prefs.flowsRowsPerPage} searchable
         onManualPageChange={onManualPageChange} pageIndex={props.pageIndex} />
      </Large>
    </Breakpoint>
  );
}

interface FlowTableProps {
    pageIndex?: number;
    onManualPageChangeFn?: (page: number) => void;
};
